import type { CSSProperties } from 'react';
import './CampaignSectionHeader.scss';
import {
  addSpaceBetweenCamelCase,
  AgxBadge,
  AgxBadgeType,
  ContractDetailModel,
  NswContractStage,
  QldContractStage,
} from '@urbanx/agx-ui-components';
import { AgxAvatarLabelGroup, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';
import { useGetUrbanXTeamForAgency, useStaffGetAgentsInAgency } from '@urbanx/agx.agencies.client';
import CrmListingBadge from './CrmListingBadge';
import { useParams } from 'react-router-dom';
import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { cleanFullAddressWithoutCountryOrPostcode } from 'helpers/address';
import { getContractStageBadgeType } from 'pages/contracts/contractStageBadge';
import contractHeaderIcon from 'assets/images/contract-header-icon.svg';

interface ContractSectionHeaderProps {
  contract: ContractDetailModel;
}

const ContractSectionHeader = ({ contract }: ContractSectionHeaderProps) => {
  const { agencyId, campaignId } = useParams();
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const { data: agents } = useStaffGetAgentsInAgency(agencyId ?? '', !!agencyId);
  const { data: urbanXTeamForAgency } = useGetUrbanXTeamForAgency(
    agencyId ?? '',
    campaign?.leadAgentId,
    !!agencyId
  );
  const contractStage = addSpaceBetweenCamelCase(contract.contractStage);
  const listingAgents = agents?.filter(
    (agent) =>
      agent.id === campaign?.leadAgentId ||
      (agent.id === campaign?.secondAgentId && agent.softDeleted === false)
  );
  return (
    <section className="contractSectionHeader">
      <AgxSpacer direction="vertical" gap="spacing-lg" style={{ width: '100%' }}>
        <AgxSpacer direction="horizontal" contentAlignment="start" gap="md">
          <AgxSpacer
            className="contractSectionHeaderIllustration"
            direction="horizontal"
            contentAlignment="center"
            contentJustification="center"
            flexShrink={0}
          >
            <img src={contractHeaderIcon} alt="" />
          </AgxSpacer>

          <AgxSpacer direction="vertical" gap="spacing-md" fill>
            {campaign?.address && (
              <AgxTypography
                id="contract-address"
                type="body_ui"
                size="small"
                color="var(--text-tertiary)"
                style={{ color: 'var(--text-tertiary)', paddingTop: 'var(--spacing-xs)' }}
              >
                {cleanFullAddressWithoutCountryOrPostcode(campaign.address)}
              </AgxTypography>
            )}

            <AgxSpacer direction="vertical" gap="xs">
              <AgxSpacer
                className="contractSectionHeaderTitleRow"
                direction="horizontal"
                contentAlignment="center"
                gap="spacing-md"
              >
                <AgxTypography id="title" type="heading" size="x-large" color="var(--text-primary)">
                  {contract.contractName}
                </AgxTypography>
                <AgxBadge
                  text={contractStage}
                  type={
                    contract.contractStage === QldContractStage.BackUp
                      ? AgxBadgeType.Finished
                      : getContractStageBadgeType(
                          contract.contractStage as QldContractStage | NswContractStage
                        )
                  }
                />
                <CrmListingBadge agencyId={agencyId} campaignId={campaignId} />
              </AgxSpacer>

              <AgxSpacer
                className="contractSectionHeaderAvatars"
                direction="horizontal"
                gap="spacing-md"
              >
                <span
                  className="agencyAvatarBranding"
                  style={
                    {
                      '--agency-avatar-background':
                        agency?.agency?.settings?.branding?.backgroundColor,
                    } as CSSProperties
                  }
                >
                  <AgxAvatarLabelGroup
                    id="contract-agency-avatar"
                    type="avatar"
                    variant="round"
                    photoUrl={agency?.agency?.settings?.branding?.logo?.publicPath}
                    label1={agency?.name ?? 'Agency'}
                    mode="letters"
                    backgroundMode="dark"
                    size="small"
                  />
                </span>
                {listingAgents?.map((agent) => (
                  <AgxAvatarLabelGroup
                    id={`contract-agent-avatar-${agent.id}`}
                    key={agent.id}
                    type="avatar"
                    variant="round"
                    photoUrl={agent.photoUrl}
                    label1={`${agent.name?.firstName} ${agent.name?.lastName}`}
                    mode="letters"
                    size="small"
                  />
                ))}
                {urbanXTeamForAgency?.adminManager && (
                  <AgxAvatarLabelGroup
                    id="contract-admin-avatar"
                    type="avatar"
                    variant="round"
                    photoUrl={urbanXTeamForAgency.adminManager.photo?.publicPath}
                    label1={`${urbanXTeamForAgency.adminManager.firstName} ${urbanXTeamForAgency.adminManager.lastName} (Admin Manager)`}
                    labelTooltip={
                      urbanXTeamForAgency.adminManagerTeamName
                        ? `Set by ${urbanXTeamForAgency.adminManagerTeamName}`
                        : 'Agency default'
                    }
                    mode="letters"
                    size="small"
                  />
                )}
              </AgxSpacer>
            </AgxSpacer>
          </AgxSpacer>
        </AgxSpacer>
      </AgxSpacer>
    </section>
  );
};

export default ContractSectionHeader;
